import type { BaseDataItem, Component } from '@wix/thunderbolt-becky-types'
import type { RefInfo } from '@wix/document-services-types'
import _ from 'lodash'

type DataRefsSchema = { [key: string]: DataRefsSchema | true }
type Refs = undefined | { [key: string]: Refs }
const assignDeep = (item: object, refs: Refs, schema: DataRefsSchema) => {
	const viewItem: Record<string, any> = { ...item }
	if (!refs || !schema) {
		return viewItem
	}
	for (const ref in schema) {
		const refSchema = schema[ref]
		if (ref in viewItem && ref in refs) {
			const refValue = refs[ref]
			if (refSchema === true) {
				// actual ref
				viewItem[ref] = refValue
			} else {
				// ancestor of referred data (e.g. ref='a', refValue={b: 'value'} , when ref path is ['a', 'b']
				// assign the refValue to the viewItem, while keeping the rest of a's properties in the dataItem
				viewItem[ref] = assignDeep(viewItem[ref], refValue, refSchema)
			}
		}
	}
	return viewItem
}

export const defaultToViewItem = (
	getSchema: (mapName: string, type: string) => RefInfo[]
) => {
	const refsSchema: any = {}

	const getRefSchema = (mapName: string, type: string) => {
		if (refsSchema[mapName]?.[type]) {
			return refsSchema[mapName][type]
		}

		const refData = getSchema(mapName, type)
		if (!refData) {
			return null
		}

		const refs = refData.reduce((acc, { path }) => {
			_.set(acc, path, true)
			return acc
		}, {})

		refsSchema[mapName] = refsSchema[mapName] || {}
		refsSchema[mapName][type] = refs

		return refs
	}

	return <T extends BaseDataItem>(mapName: string, dataItem: T, refs: Refs) => {
		const schema = getRefSchema(mapName, dataItem.type)

		const viewItem = assignDeep(dataItem, refs, schema)
		delete viewItem.metaData
		return viewItem
	}
}

const COMPONENT_TYPE_MAPPING: Record<string, string> = {
	AppPage: 'Page',
	BoxSlideShow: 'SlideShowContainer',
	BoxSlideShowSlide: 'SlideShowSlide',
	InlinePopup: 'MenuContainer',
	InlinePopupToggle: 'MenuToggle',
	MobileMerge: 'DivWithChildren',
	OldBlogDeprecation: 'Container',
	PopupCloseTextButton: 'SiteButton',
	SiteRegionContainer: 'Container',
	SlideshowItem: 'Container',
	StateBoxFormState: 'SlideShowSlide',
	StateBoxState: 'SlideShowSlide',
	StripContainerSlideShow: 'SlideShowContainer',
	StripContainerSlideShowSlide: 'SlideShowSlide',
	TwitterFeed: 'DivWithChildren',
	WGooglePlusOne: 'DivWithChildren',
	OldHoverBoxDeprecation: 'Container',
}

const mapComponentType = (componentType: string): string => COMPONENT_TYPE_MAPPING[componentType] || componentType

export const fixStructure = (component: Component): Component => {
	const { componentType, skin } = component
	const fixedComponent = { ...component, componentType: mapComponentType(_.last(componentType.split('.'))!) }

	if (skin) {
		fixedComponent.skin = skin.startsWith('svgshape.') ? skin : _.last(skin.split('.'))!
	}
	return fixedComponent
}

const EMPTY_ARRAY = [] as const
export const getChildrenAndSlots = (comp: Component): Array<string> => [
	...(comp.components || EMPTY_ARRAY),
	...(comp.slots ? Object.values(comp.slots) : EMPTY_ARRAY),
]
